/**
 * C-arm angle computation from camera position/focal point.
 *
 * Ported from C++ RegistrationTransformationUtils::computeCarmAngleFromCameraPosition.
 * Computes craniocaudal (ICC) and oblique (OA) angles from source (camera position)
 * and detector (camera focal point) in LPS coordinate space.
 */

import type { Vec3 } from './types';

export interface CarmAngles {
    readonly craniocaudal: number;
    readonly oblique: number;
}

/**
 * Mechanical envelope of the C-arm, in degrees around the frontal (AP) view.
 * Applies to every view (precomputed presets and custom views alike).
 * The angle limits are based on the reference values published in:
 * https://openheart.bmj.com/content/1/1/e000141
 */
export const CARM_LIMITS: Readonly<Record<keyof CarmAngles, number>> = {
    craniocaudal: 60,
    oblique: 100,
};

/**
 * Clamp angles into the C-arm envelope (±CARM_LIMITS per axis).
 */
export function clampCarmAngles(angles: CarmAngles): CarmAngles {
    const clamp = (value: number, limit: number): number => Math.min(limit, Math.max(-limit, value));

    return {
        craniocaudal: clamp(angles.craniocaudal, CARM_LIMITS.craniocaudal),
        oblique: clamp(angles.oblique, CARM_LIMITS.oblique),
    };
}

/**
 * Compute C-arm craniocaudal and oblique angles from camera source and detector positions.
 *
 * @param source - Camera position (clinically the detector side: angio images
 *   are viewed from the image intensifier, so the camera sits where the
 *   detector is; named "source" after the original C++ math)
 * @param detector - Camera focal point (isocenter)
 * @returns Angles in degrees. Craniocaudal is in [-180, 180]; oblique is
 *   always reported in [-90, 90] — a pose physically beyond the lateral
 *   meridian maps to its equivalent (craniocaudal ± 180°) representation,
 *   so obliques in (90, CARM_LIMITS.oblique] cannot be recovered from a
 *   camera pose, only set explicitly.
 */
export function computeCarmAngles(source: Vec3, detector: Vec3): CarmAngles {
    const vx = source[0] - detector[0];
    const vy = source[1] - detector[1];
    const vz = source[2] - detector[2];

    // Degenerate case
    if (vx === 0 && vy === 0 && vz === 0) {
        return { craniocaudal: 0, oblique: 0 };
    }

    // Pitch = PI - atan2(z, y)  →  craniocaudal
    let pitch = vz === 0 ? 0 : Math.PI - Math.atan2(vz, vy);

    // Rotate view vector around X by pitch (pitch is in radians)
    const pitchDeg = (pitch * 180) / Math.PI;
    const cosPitch = Math.cos(pitch);
    const sinPitch = Math.sin(pitch);

    // Rotation around X:  y' = y*cos - z*sin,  z' = y*sin + z*cos
    const ry = vy * cosPitch - vz * sinPitch;
    const rx = vx;

    // Yaw = atan2(ry, rx) + PI/2  →  oblique
    const yaw = Math.atan2(ry, rx) + Math.PI / 2;

    let craniocaudal = pitchDeg;
    let oblique = (yaw * 180) / Math.PI;

    // Normalize to [-180, 180]
    if (oblique > 180) oblique -= 360;
    else if (oblique < -180) oblique += 360;

    if (craniocaudal > 180) craniocaudal -= 360;
    else if (craniocaudal < -180) craniocaudal += 360;

    return { craniocaudal, oblique };
}

export interface CarmCameraPose {
    /** Camera position — clinically the detector (image intensifier) side */
    readonly position: Vec3;
    /** Target / isocenter */
    readonly focalPoint: Vec3;
    readonly viewUp: Vec3;
}

/**
 * Exact inverse of {@link computeCarmAngles}: rebuild the camera pose that
 * produces the given angles, looking at `target` from `distance` millimeters.
 *
 * The forward function flattens the source direction `v` into the XY plane by
 * pitching about X, then reads the yaw; algebraically
 * `v = Rx(-pitch) · Rz(yaw) · [0, -1, 0]` (LPS, frontal AP reference).
 * The view-up applies the same two rotations to the head-up reference
 * `[0, 0, 1]`, which reduces to `[0, sin(pitch), cos(pitch)]` — deterministic,
 * no roll ever introduced.
 *
 * Degeneracy: at oblique = ±90° the beam lies along the patient X axis, which
 * is the craniocaudal rotation axis — every craniocaudal value yields the same
 * beam direction (only the view-up differs), so {@link computeCarmAngles}
 * cannot recover craniocaudal from that pose. Round-trips are exact for
 * |oblique| < 90°; past the lateral meridian {@link computeCarmAngles} reports
 * the equivalent (craniocaudal ± 180°, 180° − oblique) representation instead
 * of the original angles.
 *
 * @param angles - Angles in degrees, already clamped into the envelope
 * @param target - Isocenter (camera focal point)
 * @param distance - Source-to-target distance in millimeters
 */
export function computeCameraFromCarmAngles(
    angles: CarmAngles,
    target: Vec3,
    distance: number
): CarmCameraPose {
    const pitch = (angles.craniocaudal * Math.PI) / 180;
    const yaw = (angles.oblique * Math.PI) / 180;

    // Rz(yaw) · [0, -1, 0] — in-plane direction before pitching
    const rx = Math.sin(yaw);
    const ry = -Math.cos(yaw);

    // Rx(-pitch) · [rx, ry, 0] — unit direction from detector to source
    const cosPitch = Math.cos(pitch);
    const sinPitch = Math.sin(pitch);
    const direction: Vec3 = [rx, ry * cosPitch, -ry * sinPitch];

    return {
        position: [
            target[0] + direction[0] * distance,
            target[1] + direction[1] * distance,
            target[2] + direction[2] * distance,
        ],
        focalPoint: [target[0], target[1], target[2]],
        viewUp: [0, sinPitch, cosPitch],
    };
}
